export const DEFAULT_PHONE_PREFIX = "+48";

/** E.164-like input used throughout the storefront: a leading + and 7–15 digits. */
export const INTERNATIONAL_PHONE_PATTERN = /^\+\d{7,15}$/;

export function isValidInternationalPhone(phone: string): boolean {
  return INTERNATIONAL_PHONE_PATTERN.test(phone.trim());
}

export function phoneValidationMessage(phone: string, required = false): string | null {
  const value = phone.trim();
  if (!value) return required ? "Podaj numer telefonu" : null;
  if (!value.startsWith("+")) return "Numer telefonu musi zaczynać się od znaku +";
  if (!/^\+\d+$/.test(value)) return "Numer telefonu może zawierać tylko znak + i cyfry";

  const digits = value.slice(1).length;
  return digits >= 7 && digits <= 15
    ? null
    : "Numer telefonu musi mieć od 7 do 15 cyfr";
}

export interface PhoneParts {
  prefix: string;
  number: string;
}

export function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, "");
  return digits.match(/.{1,3}/g)?.join(" ") ?? "";
}

export function phoneParts(fullPhone: string): PhoneParts {
  const value = fullPhone.trim();
  if (!value) return { prefix: DEFAULT_PHONE_PREFIX, number: "" };

  const digits = value.replace(/\D/g, "");
  if (!digits) return { prefix: DEFAULT_PHONE_PREFIX, number: "" };

  if (value.startsWith(DEFAULT_PHONE_PREFIX)) {
    return { prefix: DEFAULT_PHONE_PREFIX, number: digits.slice(2) };
  }

  // Calling codes are at most three digits. For an existing international value
  // this gives the user an immediately editable, sensible split.
  const prefixDigits = digits.slice(0, Math.min(3, digits.length));
  return { prefix: `+${prefixDigits}`, number: digits.slice(prefixDigits.length) };
}

export function normalizePrefix(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 3);
  return digits ? `+${digits}` : "";
}

export function combinePhone(prefix: string, number: string): string {
  const normalizedPrefix = normalizePrefix(prefix);
  const digits = number.replace(/\D/g, "").slice(0, Math.max(0, 15 - normalizedPrefix.replace(/\D/g, "").length));

  // The default visible prefix is not a user-entered phone number until there
  // are local digits; this keeps optional fields genuinely empty.
  if (!digits && normalizedPrefix === DEFAULT_PHONE_PREFIX) return "";
  return `${normalizedPrefix}${digits}`;
}